import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, CircularProgress, Paper, Stack, Button, TextField,
  Grid, Chip, Alert, Snackbar, Stepper, Step, StepLabel, MenuItem, InputAdornment,
} from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../../api';
import type { MeColleague, MeDepartment, MeOrganizationLocation, MeProfile } from '../../api/me';
import ProcessAttachmentField, { type StagedProcessAttachment } from './ProcessAttachmentField';
import ProcessFormResourceValue from './ProcessFormResourceValue';
import { buildProcessRequestFormData } from './processRequestSubmission';
import { ColleaguePickerMulti, ColleaguePickerSingle } from '../../components/worker/ColleaguePicker';
import { PlaceSearchField } from '../../components/location';
import DateField from '../../components/inputs/DateField';
import { useTranslation } from 'react-i18next';
import {
  ownRequestDataSource,
  type SubmitRequestDataSource,
} from './submitRequestDataSource';

import { PORTAL_FORM_MAX_WIDTH } from '../../theme/shellMetrics';
import { translateApiError } from '../../i18n/translateApiError';
import {
  dateFieldIncludesTime,
  departmentDisplayName,
  formatDurationMinutes,
  multiSelectValues,
  parseDurationMinutes,
  derivedDayFields,
  joinDateTime,
  splitDateTime,
  localizedProcessDescription,
  localizedProcessFieldLabel,
  localizedProcessFieldOptions,
  localizedProcessFieldText,
  localizedProcessFieldValue,
  localizedProcessName,
  processFormFieldKey,
  processFormFieldKind,
} from './processFormContract';
import { approvalTargetHours, formatApprovalTargetHours } from '../Processes/approvalTarget';

interface FormField {
  id?: string;
  type?: string;
  label?: string;
  required?: boolean;
  properties?: {
    label?: string;
    placeholder?: string;
    required?: boolean;
    options?: string[] | { label: string; value: string }[];
    [k: string]: unknown;
  };
}

interface ProcessDefinition {
  id: string;
  name: string;
  description?: string | null;
  category?: string | null;
  organization_currency?: string;
  system_key?: string | null;
  form_schema?: { system_key?: string | null; fields?: FormField[] } | null;
  flow?: {
    nodes?: Array<{ id: string; type: string; data?: { label?: string; config?: any } }>;
    edges?: Array<{ id: string; source: string; target: string; sourceHandle?: string }>;
  } | null;
}

type ToastState = {
  message: string;
  severity: 'success' | 'error' | 'info';
};

export function editableResubmitFormData(
  sourceData: Record<string, unknown> | null | undefined,
  fields: FormField[],
): Record<string, unknown> {
  if (!sourceData) return {};
  const editable: Record<string, unknown> = {};
  fields.forEach((field, index) => {
    const kind = processFormFieldKind(field.type);
    // `worker` / `department` are excluded for the SAME reason as `location`: the
    // stored value is a server SNAPSHOT object, and the submit path accepts only an
    // id string, so copying it forward turns a resubmit into a 400. `staticText`
    // holds no value at all and the backend refuses one (2026-08-25).
    if (
      kind === 'attachment' || kind === 'location' || kind === 'unsupported'
      || kind === 'worker' || kind === 'department' || kind === 'staticText'
    ) return;
    const key = processFormFieldKey(field, index);
    if (Object.prototype.hasOwnProperty.call(sourceData, key)) editable[key] = sourceData[key];
  });
  return editable;
}

/**
 * `source` decides WHO the request is being filed for. Omitted, it is the signed-in worker and every call
 * below behaves exactly as it did before — `ownRequestDataSource()` is the identity implementation, which
 * is why this extraction changed no behaviour on the worker portal.
 *
 * An admin filing on a worker's behalf passes `onBehalfRequestDataSource(workerId)`: the picker then lists
 * what the TARGET may start (`can_worker_initiate_definition` runs against the requester), the auto-filled
 * name is the requester's rather than the admin's, and the POST goes to the admin route which records
 * `created_by` (alembic 120).
 */
const WorkerSubmitRequest: React.FC<{
  source?: SubmitRequestDataSource;
  /** Called instead of navigating, when the form is embedded (the admin on-behalf dialog). */
  onFiled?: () => void;
}> = ({ source, onFiled }) => {
  const ds = React.useMemo(() => source ?? ownRequestDataSource(), [source]);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedDefinitionId = searchParams.get('definitionId');
  const resubmitFrom = searchParams.get('resubmitFrom');
  const [loading, setLoading] = useState(true);
  const [processes, setProcesses] = useState<ProcessDefinition[]>([]);
  const [profile, setProfile] = useState<MeProfile | null>(null);
  const [resubmitSource, setResubmitSource] = useState<any>(null);
  const [selected, setSelected] = useState<ProcessDefinition | null>(null);
  const [step, setStep] = useState(0);
  const [title, setTitle] = useState('');
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  // The RAW text of every duration field, keyed by field. Kept beside `formData`
  // rather than inside it because `formData` holds what gets SUBMITTED (minutes),
  // and re-deriving the text from minutes on every keystroke makes an intermediate
  // value like `2:` or `2:0` unreachable — 2:05 could not be typed at all.
  const [durationText, setDurationText] = useState<Record<string, string>>({});
  const [locations, setLocations] = useState<MeOrganizationLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState(false);
  const [departments, setDepartments] = useState<MeDepartment[]>([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [departmentsError, setDepartmentsError] = useState(false);
  // The picker needs the whole colleague row to render its own chip; `formData`
  // must hold only the id, because that is all the submit path accepts.
  const [workerPicked, setWorkerPicked] = useState<Record<string, MeColleague | null>>({});
  const [stagedAttachments, setStagedAttachments] = useState<Record<string, StagedProcessAttachment>>({});
  const [busyAttachmentFields, setBusyAttachmentFields] = useState<Set<string>>(new Set());
  const [attachmentResourceErrors, setAttachmentResourceErrors] = useState<Set<string>>(new Set());
  const [priority, setPriority] = useState('medium');
  const [ccPicked, setCcPicked] = useState<MeColleague[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const showError = useCallback((caught: unknown, fallbackKey: string) => {
    setToast({
      message: caught instanceof ApiError ? translateApiError(caught, t) : t(fallbackKey),
      severity: 'error',
    });
  }, [t]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      // Profile fetch is best-effort — without it, worker-name
      // auto-fill (Issue 3.2) and title auto-generation (Issue 3.1)
      // fall back to legacy behaviour. Don't block process picker on
      // a profile network blip.
      const [data, p, source] = await Promise.all([
        ds.listAvailableProcesses(),
        ds.getProfile(),
        resubmitFrom ? ds.getRequest(resubmitFrom) : Promise.resolve(null),
      ]);
      const available = Array.isArray(data) ? (data as ProcessDefinition[]) : [];
      setResubmitSource(source);
      // A resubmission is bound to the source request's definition. The URL is
      // navigation input only and must never retarget validation or upload intents.
      // Missing/unavailable source definitions fail closed by selecting nothing.
      const requestedDefinitionId = resubmitFrom
        ? (source as any)?.definition_id
        : preselectedDefinitionId;
      const requested = requestedDefinitionId
        ? available.find((definition) => definition.id === requestedDefinitionId)
        : undefined;
      setProcesses(resubmitFrom ? (requested ? [requested] : []) : available);
      if (requested) {
        setSelected(requested);
        setFormData(editableResubmitFormData(
          (source as any)?.data as Record<string, unknown> | undefined,
          requested.form_schema?.fields ?? [],
        ));
        setStagedAttachments({});
        setBusyAttachmentFields(new Set());
        setAttachmentResourceErrors(new Set());
        setWorkerPicked({});
        setPriority((source as any)?.priority ?? 'medium');
        setStep(1);
      }
      setProfile(p);
    } catch (caught) {
      setProcesses([]);
      showError(caught, 'common:feedback.loadFailed');
    } finally {
      setLoading(false);
    }
  }, [preselectedDefinitionId, resubmitFrom, showError, ds]);

  useEffect(() => { fetch(); }, [fetch]);

  const fields = useMemo(() => selected?.form_schema?.fields ?? [], [selected]);
  const needsLocations = useMemo(
    () => fields.some((field) => processFormFieldKind(field.type) === 'location'),
    [fields],
  );

  const fetchLocations = useCallback(async () => {
    if (!needsLocations) {
      setLocations([]);
      setLocationsError(false);
      return;
    }
    setLocationsLoading(true);
    try {
      const activeLocations = await ds.listActiveLocations();
      setLocations(activeLocations);
      setLocationsError(false);
    } catch (caught) {
      setLocations([]);
      setLocationsError(true);
      showError(caught, 'worker:submitRequest.locationsLoadFailed');
    } finally {
      setLocationsLoading(false);
    }
  }, [needsLocations, showError, ds]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  // Departments follow the location shape exactly: an org-scoped read the field
  // needs before it can offer anything, fetched only when the schema has such a
  // field. Kept as its own state rather than folded into `locations` because the
  // two lists are different rows and a shared error flag would block a form whose
  // only reference field loaded fine.
  const needsDepartments = useMemo(
    () => fields.some((field) => processFormFieldKind(field.type) === 'department'),
    [fields],
  );

  const fetchDepartments = useCallback(async () => {
    if (!needsDepartments) {
      setDepartments([]);
      setDepartmentsError(false);
      return;
    }
    setDepartmentsLoading(true);
    try {
      const activeDepartments = await ds.listActiveDepartments();
      setDepartments(activeDepartments);
      setDepartmentsError(false);
    } catch (caught) {
      setDepartments([]);
      setDepartmentsError(true);
      showError(caught, 'worker:submitRequest.departmentsLoadFailed');
    } finally {
      setDepartmentsLoading(false);
    }
  }, [needsDepartments, showError, ds]);

  useEffect(() => { fetchDepartments(); }, [fetchDepartments]);

  const handlePickProcess = (p: ProcessDefinition) => {
    // Path C 2026-05-22 — preserve form data when the user is just
    // navigating back-and-forth on the SAME process (Step 0 → Step 1
    // → Step 0 → click same process again). Pre-fix this wiped
    // formData unconditionally, which destroyed half-typed forms
    // every time the user wanted to glance at the process picker.
    // Switching to a different process still wipes (the new schema
    // has different fields → keeping stale values would mismatch).
    //
    // Title is now driven by the autoTitle useEffect (Issue 3.1) so
    // we don't seed it manually here.
    if (selected?.id !== p.id) {
      setFormData({});
      setStagedAttachments({});
      setBusyAttachmentFields(new Set());
      setAttachmentResourceErrors(new Set());
      setLocationsError(false);
      setDepartmentsError(false);
      setWorkerPicked({});
    }
    setSelected(p);
    setStep(1);
  };

  const handleSubmit = async () => {
    if (!selected || !title.trim() || !canSubmit || busyAttachmentFields.size > 0) return;
    setSubmitting(true);
    try {
      // Backend binds attachment IDs from form_data[field_id]. Keep the explicit
      // map here so a staged file can never be confused with a display value.
      const submissionData = buildProcessRequestFormData(formData, stagedAttachments);
      const submitted = resubmitFrom
        ? await ds.resubmitRequest(resubmitFrom, {
          form_data: Object.keys(submissionData).length > 0 ? submissionData : undefined,
        }) as any
        : await ds.createRequest({
          process_definition_id: selected.id,
          title: title.trim(),
          priority,
          form_data: Object.keys(submissionData).length > 0 ? submissionData : undefined,
          cc_worker_ids: ccPicked.length > 0 ? ccPicked.map((c) => c.worker_id) : undefined,
        }) as any;
      setToast({
        message: t(resubmitFrom
          ? 'worker:requestDetail.toastResubmitted'
          : 'worker:submitRequest.toastSubmitted'),
        severity: 'success',
      });
      // WHERE TO GO AFTERWARDS depends on who filed it. A worker lands on their own request list; an
      // admin filing on someone's behalf is inside a dialog on the All Requests page, and sending them to
      // `/me/requests` would drop them into the worker portal — which `PortalBoundary` then redirects out
      // of, so the submission would look like it failed (svdy-ui §12.10).
      if (onFiled) {
        setTimeout(onFiled, 600);
      } else {
        setTimeout(() => navigate(
          resubmitFrom && submitted?.id ? `/me/requests/${submitted.id}` : '/me/requests',
        ), 600);
      }
    } catch (e) {
      showError(e, 'common:feedback.submitFailed');
      setSubmitting(false);
    }
  };

  const updateField = (key: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  // Resolve form_data dict key for a schema field.
  //
  // Order: id → properties.label → label → field_{idx}. Earlier
  // P0.4 used label-first which orphaned stored form_data whenever
  // an admin renamed a field. ProcessDesigner now always emits a
  // stable id (step-restored-N etc.) so we prefer it; falls back to
  // label / index for legacy schemas authored before the id became
  // mandatory. The backend (app/processes/form_validation.py
  // ::_field_key) mirrors this order — update in lockstep.

  // Issue 3.2 (Path C 2026-05-22) — auto-fill worker_name fields.
  // Detect form fields whose id / label looks like a "who is the
  // requestor" placeholder so we can pre-fill from the current
  // worker's profile and lock them read-only. Pattern matches
  // worker_name / employee_name / requestor / applicant / your name.
  // Reused for read-only state during render (so the user can see
  // the value but can't accidentally type over it).
  const isWorkerNameField = (f: FormField): boolean => {
    const haystack = `${f.id ?? ''} ${f.label ?? ''} ${f.properties?.label ?? ''}`.toLowerCase();
    if (/(worker|employee|requestor|requester|applicant)[\s_-]?name/.test(haystack)) return true;
    if (/^(your[\s_-])?name$/.test(haystack.trim())) return true;
    return false;
  };

  // Issue 3.3 (Path C 2026-05-22) — date / days_required field
  // detection. Mirrors the worker-name pattern above; consumed by
  // the auto-compute effect that calls /v1/me/business-days when
  // both date fields are filled.
  //
  // Detection: lowercased haystack of (id + label + properties.label)
  // matched against canonical leave-style field keywords. Conservative
  // patterns — we want false-negatives over false-positives so a
  // mis-named generic-text field can't accidentally lock the user
  // out of editing it.
  const isStartDateField = (f: FormField): boolean => {
    const haystack = `${f.id ?? ''} ${f.label ?? ''} ${f.properties?.label ?? ''}`.toLowerCase();
    return /(start|from|begin)[\s_-]?date|^start$|leave[\s_-]?start/.test(haystack);
  };
  const isEndDateField = (f: FormField): boolean => {
    const haystack = `${f.id ?? ''} ${f.label ?? ''} ${f.properties?.label ?? ''}`.toLowerCase();
    return /(end|to|finish|return)[\s_-]?date|^end$|leave[\s_-]?end/.test(haystack);
  };
  const isDaysRequiredField = (f: FormField): boolean => {
    const haystack = `${f.id ?? ''} ${f.label ?? ''} ${f.properties?.label ?? ''}`.toLowerCase();
    return /(days|duration)[\s_-]?(required|needed|count)?|leave[\s_-]?days|^days$/.test(haystack);
  };

  const workerDisplayName = useMemo(() => {
    if (!profile) return '';
    return `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim();
  }, [profile]);

  // Auto-seed form fields when the schema or profile changes. The
  // effect runs on every (selected, profile) pair so first-paint
  // (profile loaded after selected) and re-pick-same-process both
  // refresh worker-name fields without overwriting user-typed
  // values in OTHER fields.
  useEffect(() => {
    if (!selected || !profile) return;
    const schemaFields = selected.form_schema?.fields ?? [];
    const updates: Record<string, unknown> = {};
    schemaFields.forEach((f, i) => {
      if (!isWorkerNameField(f)) return;
      const key = processFormFieldKey(f, i);
      // Always re-seed — these fields are read-only so the value
      // can't have been changed by the user; if profile changed
      // (rare) the displayed value should track.
      updates[key] = workerDisplayName;
    });
    if (Object.keys(updates).length > 0) {
      setFormData((prev) => ({ ...prev, ...updates }));
    }
  }, [selected, profile, workerDisplayName]);

  // Issue 3.3 (Path C 2026-05-22) — locate the schema's start_date /
  // end_date / days_required fields once per process change. The
  // resolution is deterministic so we memoize keys here and reuse
  // them in the auto-compute effect + the read-only render branch.
  const dateRangeKeys = useMemo(() => {
    if (!selected) return null;
    // The SCHEMA decides, when it says so. `derived_from_dates` is declared on the field
    // and read by the server, which overwrites the submitted value — so guessing by name
    // was a second source of truth for the same fact (svdy-rules §10.5b, 2026-08-24).
    const declared = Object.entries(derivedDayFields(selected.form_schema));
    if (declared.length > 0) {
      const [daysKey, [startKey, endKey]] = declared[0];
      return { startKey, endKey, daysKey };
    }
    // LEGACY fallback: a definition authored before the property existed. The regexes
    // below guess which number is a day count from its id and label, which can fire on
    // the wrong field — kept only so those definitions keep auto-filling.
    const schemaFields = selected.form_schema?.fields ?? [];
    let startKey: string | null = null;
    let endKey: string | null = null;
    let daysKey: string | null = null;
    schemaFields.forEach((f, i) => {
      const k = processFormFieldKey(f, i);
      if (!startKey && isStartDateField(f)) startKey = k;
      if (!endKey && isEndDateField(f)) endKey = k;
      if (!daysKey && isDaysRequiredField(f)) daysKey = k;
    });
    return startKey && endKey && daysKey ? { startKey, endKey, daysKey } : null;
  }, [selected]);

  // Auto-compute days_required from start_date + end_date. Calls
  // /v1/me/business-days which respects the org's AttendanceRule
  // rest_days + PublicHoliday calendar. The result populates the
  // days field as read-only so the worker can see the count without
  // hand-counting weekends and holidays.
  //
  // Best-effort: if the network call fails (license, 503, etc.),
  // the days field falls back to inclusive calendar-day diff so the
  // form is still submittable.
  useEffect(() => {
    if (!dateRangeKeys) return;
    const { startKey, endKey, daysKey } = dateRangeKeys;
    const startVal = formData[startKey] as string | undefined;
    const endVal = formData[endKey] as string | undefined;
    if (!startVal || !endVal) return;
    // Cheap pre-check — if start > end, skip the API call (backend
    // would 400 anyway, no need to surface that to the user mid-typing).
    if (startVal > endVal) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await ds.getBusinessDays(startVal, endVal, false);
        if (cancelled) return;
        setFormData((prev) => ({ ...prev, [daysKey]: String(r.business_days) }));
      } catch {
        if (cancelled) return;
        // Fallback — inclusive calendar diff. Not as accurate (counts
        // weekends/holidays as business days) but at least populates
        // a value so submission isn't blocked.
        const ms = new Date(endVal).getTime() - new Date(startVal).getTime();
        const fallback = Math.max(1, Math.round(ms / 86400000) + 1);
        setFormData((prev) => ({ ...prev, [daysKey]: String(fallback) }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dateRangeKeys, formData, ds]);

  // Issue 3.1 (Path C 2026-05-22) — auto-generated request title.
  // Format mirrors common SMB workflow tools (Linear / Asana):
  //   "<worker_code> <full_name> — <process_name>"
  // Read-only on the form so workers don't second-guess the format
  // and admins downstream get consistent titles for filter/search.
  // Falls back to the legacy `process.name` when profile hasn't
  // loaded yet (best-effort fetch).
  const autoTitle = useMemo(() => {
    if (resubmitSource?.title) return resubmitSource.title;
    if (!selected) return '';
    const processName = localizedProcessName(selected, t);
    if (!profile) return processName;
    const code = profile.worker_code ? `[${profile.worker_code}] ` : '';
    const name = workerDisplayName ? `${workerDisplayName} — ` : '';
    return `${code}${name}${processName}`;
  }, [selected, profile, workerDisplayName, resubmitSource, t]);

  // Keep `title` state in sync with `autoTitle` whenever the source
  // changes. Since the field is read-only, we don't need to debounce
  // user input — autoTitle is the only writer.
  useEffect(() => {
    if (autoTitle) setTitle(autoTitle);
  }, [autoTitle]);

  // Client-side required gate (svdy-ui.md §8). Backend P0.4
  // form_validation is the second gate, but blocking in the UI gives
  // a clearer signal than the round-trip 400.
  const canSubmit = useMemo(() => {
    if (!selected || !title.trim()) return false;
    if (locationsError || departmentsError || attachmentResourceErrors.size > 0) return false;
    for (let i = 0; i < fields.length; i++) {
      const f = fields[i];
      const kind = processFormFieldKind(f.type);
      // A guidance note collects nothing, and the backend REFUSES a value for it.
      // Without this skip a `required: true` left on a legacy note would block the
      // form forever — there is no input that could satisfy it.
      if (kind === 'staticText') continue;
      const required = Boolean(f.properties?.required ?? f.required);
      if (!required) continue;
      const key = processFormFieldKey(f, i);
      const v = kind === 'attachment' ? stagedAttachments[key]?.id : formData[key];
      if (v == null || (typeof v === 'string' && v.trim() === '')) return false;
    }
    return busyAttachmentFields.size === 0;
  }, [
    selected, title, fields, formData, stagedAttachments, busyAttachmentFields,
    locationsError, departmentsError, attachmentResourceErrors,
  ]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            {t(resubmitFrom ? 'worker:requestDetail.resubmit' : 'worker:submitRequest.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {step === 0 ? t('worker:submitRequest.pickProcess') : step === 1 ? t('worker:submitRequest.fillForm', { name: selected ? localizedProcessName(selected, t) : '' }) : t('worker:submitRequest.reviewChain')}
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
          onClick={() => navigate(resubmitFrom ? `/me/requests/${resubmitFrom}` : '/me/requests')}
          sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
        >
          {t('worker:submitRequest.backToRequests')}
        </Button>
      </Box>

      <Box sx={{ width: '100%', maxWidth: PORTAL_FORM_MAX_WIDTH, mb: 2.5 }}>
        <Stepper activeStep={step} alternativeLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.8125rem' } }}>
          <Step><StepLabel>{t('worker:submitRequest.stepPick')}</StepLabel></Step>
          <Step><StepLabel>{t('worker:submitRequest.stepFill')}</StepLabel></Step>
          <Step><StepLabel>{t('worker:submitRequest.stepReview')}</StepLabel></Step>
        </Stepper>
      </Box>

      {step === 0 && (
        <Box sx={{ width: '100%' }}>
          {processes.length === 0 ? (
            <Paper elevation={0} sx={{ width: '100%', maxWidth: PORTAL_FORM_MAX_WIDTH, p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">{t('worker:submitRequest.noProcesses')}</Typography>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                {t('worker:submitRequest.noDefinitionsHint')}
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2} sx={{ maxWidth: PORTAL_FORM_MAX_WIDTH }}>
              {processes.map((p) => (
                <Grid item xs={12} sm={6} key={p.id}>
                  <Paper
                    elevation={0}
                    onClick={() => handlePickProcess(p)}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      transition: 'border-color 120ms',
                      '&:hover': { borderColor: 'primary.main' },
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{localizedProcessName(p, t)}</Typography>
                      {p.category && (
                        <Chip
                          label={p.category}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: 1, height: 18, fontSize: '0.65rem' }}
                        />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      {localizedProcessDescription(p, t) || 'No description.'}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {step === 1 && selected && (
        <Paper elevation={0} sx={{ width: '100%', maxWidth: PORTAL_FORM_MAX_WIDTH, p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={2}>
            {/* Issue 3.1 (Path C 2026-05-22) — title is auto-generated
                from worker code + name + process name. Read-only so
                downstream admins / approvers see consistent titles for
                filter/search; the worker doesn't have to invent
                wording every time. */}
            <TextField
              label={t('worker:submitRequest.labelTitle')}
              value={title}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              helperText={t('worker:submitRequest.helperAutoTitle')}
            />
            <TextField
              select
              label={t('worker:submitRequest.labelPriority')}
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              disabled={Boolean(resubmitFrom)}
              size="small"
            >
              <MenuItem value="low">{t('worker:submitRequest.priorityLow')}</MenuItem>
              <MenuItem value="medium">{t('worker:submitRequest.priorityMedium')}</MenuItem>
              <MenuItem value="high">{t('worker:submitRequest.priorityHigh')}</MenuItem>
              <MenuItem value="critical">{t('worker:submitRequest.priorityCritical')}</MenuItem>
            </TextField>

            {fields.length > 0 ? (
              /* A 12-column grid, because `properties.colSpan` is a real layout instruction
                 and this form — the one a requester actually fills in — used to ignore it.
                 Measured 2026-08-28: `colSpan` was read by the Designer CANVAS alone, so
                 "half width" moved boxes on an admin's design surface and reached nobody.
                 
                 The span is applied by WRAPPING each rendered field rather than by editing
                 the ~370-line branch below: `rendered[i]` corresponds to `fields[i]` by
                 construction, so the mapping is exact, and a branch that returns `null`
                 cannot desync it the way `Children.map` would.
                 
                 RESPONSIVE: full width below `sm`. `colSpan: 6` means "half width where there
                 is room", and a phone never has room — which is also why the mobile app needs
                 no change. One rule, two viewports. */
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(12, 1fr)',
                  columnGap: 2,
                  rowGap: 2,
                }}
              >
                {fields.map((f, i) => {
                const key = processFormFieldKey(f, i);
                const label = localizedProcessFieldLabel(selected.form_schema, f, i, t);
                const required = f.properties?.required || f.required;
                const kind = processFormFieldKind(f.type);
                const isAutoFilledName = isWorkerNameField(f);
                // Issue 3.3 — auto-computed days field is read-only
                // ONLY when the schema also has start_date + end_date
                // fields (so the worker can rely on the computed
                // value). For schemas without dates the days field
                // stays user-editable.
                const isAutoComputedDays =
                  dateRangeKeys?.daysKey === key && !!dateRangeKeys;

                if (kind === 'staticText') {
                  // NOT a field. It collects nothing and the backend rejects a value
                  // for it, so the fallback text box below did not merely look wrong
                  // — it made Leave Request unsubmittable the moment anyone typed in
                  // the note. Same read-only shape as the admin preview.
                  const noteText = localizedProcessFieldText(selected.form_schema, f, t);
                  return (
                    <Box key={key} sx={{ py: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>{label}</Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ whiteSpace: 'pre-line' }}
                      >
                        {noteText}
                      </Typography>
                    </Box>
                  );
                }
                if (kind === 'worker') {
                  // `includeSelf` — the requester may be the subject of the form
                  // (Offboarding names a `Departing Worker` and a resignation is
                  // filed by that person). CC keeps the opposite default.
                  return (
                    <ColleaguePickerSingle
                      key={key}
                      label={label}
                      required={required}
                      includeSelf
                      helperText={t('worker:submitRequest.workerReferenceHelper')}
                      value={workerPicked[key] ?? null}
                      onChange={(picked) => {
                        setWorkerPicked((previous) => ({ ...previous, [key]: picked }));
                        // Only the id is submitted; the server resolves it and stores
                        // its own snapshot.
                        setFormData((previous) => {
                          const next = { ...previous };
                          if (picked) next[key] = picked.worker_id;
                          else delete next[key];
                          return next;
                        });
                      }}
                    />
                  );
                }
                if (kind === 'department') {
                  return (
                    <Box key={key}>
                      {departmentsError && (
                        <Alert severity="error" sx={{ mb: 1 }} action={
                          <Button color="inherit" size="small" onClick={fetchDepartments}>{t('common:actions.retry')}</Button>
                        }>
                          {t('worker:submitRequest.departmentsLoadFailed')}
                        </Alert>
                      )}
                      <TextField
                        select
                        label={label}
                        value={(formData[key] as string) || ''}
                        onChange={(event) => updateField(key, event.target.value)}
                        size="small"
                        required={required}
                        fullWidth
                        disabled={departmentsLoading || departmentsError}
                        helperText={departmentsLoading
                          ? t('worker:submitRequest.departmentsLoading')
                          : t('worker:submitRequest.departmentHelper')}
                      >
                        {departments.map((department) => (
                          <MenuItem key={department.id} value={department.id}>
                            {departmentDisplayName(department)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  );
                }
                if (kind === 'place') {
                  // The SAME component the attendance request form uses, so a worker
                  // filling "where are you going" gets one behaviour across both
                  // modules. It degrades to a plain text box without a Maps key.
                  return (
                    <PlaceSearchField
                      key={key}
                      label={label}
                      required={required}
                      value={(formData[key] as string) || ''}
                      onChange={(next) => updateField(key, next)}
                    />
                  );
                }
                if (kind === 'date' && dateFieldIncludesTime(f)) {
                  // TWO controls, not one `datetime-local`: this reuses the DatePicker whose
                  // calendar follows the app's i18n language (the reason `DateField` exists
                  // at all) and adds a plain time input beside it. A native
                  // `datetime-local` would reintroduce the browser-locale calendar.
                  const { day, time } = splitDateTime(formData[key]);
                  return (
                    <Box key={key} sx={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 1 }}>
                      <DateField
                        label={label}
                        value={day}
                        onChange={(event) => updateField(key, joinDateTime(event.target.value, time))}
                        size="small"
                        required={required}
                        fullWidth
                      />
                      <TextField
                        type="time"
                        label={t('worker:submitRequest.timeLabel')}
                        value={time}
                        onChange={(event) => updateField(key, joinDateTime(day, event.target.value))}
                        size="small"
                        required={required}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                  );
                }
                if (kind === 'date') {
                  return (
                    <DateField
                      key={key}
                      label={label}
                      value={(formData[key] as string) || ''}
                      onChange={(e) => updateField(key, e.target.value)}
                      size="small"
                      required={required}
                      fullWidth
                    />
                  );
                }
                const legacyCurrency = kind === 'number' && /amount|budget|cost|value/i.test(label);
                const currencyCode = String(
                  f.properties?.currency ?? selected.organization_currency ?? 'USD',
                ).toUpperCase();
                if (kind === 'number' || kind === 'currency') {
                  const displaysCurrency = kind === 'currency' || legacyCurrency;
                  return (
                    <TextField
                      key={key}
                      type="number"
                      label={label}
                      value={(formData[key] as string) || ''}
                      onChange={(e) =>
                        isAutoComputedDays ? undefined : updateField(key, e.target.value)
                      }
                      size="small"
                      required={required}
                      fullWidth
                      InputProps={{
                        ...(isAutoComputedDays ? { readOnly: true } : {}),
                        ...(displaysCurrency ? {
                          endAdornment: <InputAdornment position="end">{currencyCode}</InputAdornment>,
                        } : {}),
                      }}
                      helperText={
                        isAutoComputedDays
                          ? t('worker:submitRequest.autoComputedDays')
                          : undefined
                      }
                    />
                  );
                }
                if (kind === 'textarea') {
                  return (
                    <TextField
                      key={key}
                      label={label}
                      value={(formData[key] as string) || ''}
                      onChange={(e) => updateField(key, e.target.value)}
                      size="small"
                      required={required}
                      fullWidth
                      multiline
                      minRows={3}
                    />
                  );
                }
                if (kind === 'select') {
                  const optArr = localizedProcessFieldOptions(selected.form_schema, f, t);
                  return (
                    <TextField
                      key={key}
                      select
                      label={label}
                      value={(formData[key] as string) || ''}
                      onChange={(e) => updateField(key, e.target.value)}
                      size="small"
                      required={required}
                      fullWidth
                    >
                      {optArr.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </TextField>
                  );
                }
                if (kind === 'multiSelect') {
                  const optArr = localizedProcessFieldOptions(selected.form_schema, f, t);
                  const chosen = multiSelectValues(formData[key]);
                  return (
                    <TextField
                      key={key}
                      select
                      label={label}
                      value={chosen}
                      onChange={(e) => {
                        // MUI hands a multiple Select its value as an array already; the
                        // string branch is the RTL/autofill path where one value arrives
                        // bare. Stored as an array either way, because the server's only
                        // list-valued field must not sometimes hold a scalar.
                        const raw = e.target.value as unknown;
                        updateField(key, Array.isArray(raw) ? raw : multiSelectValues(raw));
                      }}
                      size="small"
                      required={required}
                      fullWidth
                      SelectProps={{
                        multiple: true,
                        // The chips render the LABELS, not the stored values: an option
                        // whose label is localized would otherwise show its raw key.
                        renderValue: (value) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(value as string[]).map((v) => (
                              <Chip
                                key={v}
                                label={optArr.find((o) => o.value === v)?.label ?? v}
                                size="small"
                                sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }}
                              />
                            ))}
                          </Box>
                        ),
                      }}
                      helperText={t('worker:submitRequest.multiSelectHelper')}
                    >
                      {optArr.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </TextField>
                  );
                }
                if (kind === 'duration') {
                  const stored = formData[key];
                  return (
                    <TextField
                      key={key}
                      label={label}
                      // The RAW text stays in state while the requester types, because
                      // round-tripping every keystroke through minutes makes "2:" and "2:0"
                      // unreachable — you could not type 2:05 at all.
                      value={durationText[key] ?? (typeof stored === 'number' ? formatDurationMinutes(stored) : '')}
                      onChange={(e) => {
                        const text = e.target.value;
                        setDurationText((prev) => ({ ...prev, [key]: text }));
                        const minutes = parseDurationMinutes(text);
                        updateField(key, minutes === null ? '' : minutes);
                      }}
                      size="small"
                      required={required}
                      fullWidth
                      placeholder={t('worker:submitRequest.durationPlaceholder')}
                      error={!!durationText[key] && parseDurationMinutes(durationText[key]) === null}
                      helperText={t('worker:submitRequest.durationHelper')}
                    />
                  );
                }
                if (kind === 'location') {
                  return (
                    <Box key={key}>
                      {locationsError && (
                        <Alert severity="error" sx={{ mb: 1 }} action={
                          <Button color="inherit" size="small" onClick={fetchLocations}>{t('common:actions.retry')}</Button>
                        }>
                          {t('worker:submitRequest.locationsLoadFailed')}
                        </Alert>
                      )}
                      <TextField
                        select
                        label={label}
                        value={(formData[key] as string) || ''}
                        onChange={(event) => updateField(key, event.target.value)}
                        size="small"
                        required={required}
                        fullWidth
                        disabled={locationsLoading || locationsError}
                        helperText={locationsLoading
                          ? t('worker:submitRequest.locationsLoading')
                          : t('worker:submitRequest.locationHelper')}
                      >
                        {locations.map((location) => (
                          <MenuItem key={location.id} value={location.id}>
                            {location.name}{location.address ? ` — ${location.address}` : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  );
                }
                if (kind === 'attachment') {
                  return (
                    <ProcessAttachmentField
                      key={key}
                      definitionId={selected.id}
                      fieldId={key}
                      label={label}
                      required={required}
                      documentKind={typeof f.properties?.document_kind === 'string' ? f.properties.document_kind : undefined}
                      accept={typeof f.properties?.accept === 'string' ? f.properties.accept : undefined}
                      allowedTypes={f.properties?.allowedTypes}
                      maxBytes={typeof f.properties?.max_bytes === 'number' ? f.properties.max_bytes : undefined}
                      value={stagedAttachments[key]}
                      onChange={(attachment) => {
                        setStagedAttachments((previous) => {
                          const next = { ...previous };
                          if (attachment) next[key] = attachment;
                          else delete next[key];
                          return next;
                        });
                        setFormData((previous) => {
                          const next = { ...previous };
                          if (attachment) next[key] = attachment.id;
                          else delete next[key];
                          return next;
                        });
                      }}
                      onBusyChange={(busy) => setBusyAttachmentFields((previous) => {
                        const next = new Set(previous);
                        if (busy) next.add(key); else next.delete(key);
                        return next;
                      })}
                      onErrorChange={(hasError) => setAttachmentResourceErrors((previous) => {
                        const next = new Set(previous);
                        if (hasError) next.add(key); else next.delete(key);
                        return next;
                      })}
                    />
                  );
                }
                if (kind === 'unsupported') {
                  return <Alert severity="error" key={key}>{t('common:feedback.submitFailed')}</Alert>;
                }
                return (
                  <TextField
                    key={key}
                    label={label}
                    value={(formData[key] as string) || ''}
                    onChange={(e) => updateField(key, e.target.value)}
                    size="small"
                    required={required}
                    fullWidth
                    placeholder={(f.properties?.placeholder as string) || undefined}
                    InputProps={isAutoFilledName ? { readOnly: true } : undefined}
                    helperText={
                      isAutoFilledName
                        ? 'Auto-filled from your worker profile.'
                        : undefined
                    }
                  />
                );
                }).map((node, index) => {
                  const raw = Number(
                    (fields[index]?.properties as Record<string, unknown> | undefined)?.colSpan,
                  );
                  // Outside the grid falls back to full width rather than a sliver: a legacy
                  // field carries no span, and a hand-written schema could carry any number.
                  const span = raw >= 1 && raw <= 12 ? raw : 12;
                  return (
                    <Box
                      key={processFormFieldKey(fields[index], index)}
                      // See ProcessFormPreview: exposed so the span can be asserted in
                      // jsdom, which does not evaluate the `sm` media query.
                      data-col-span={span}
                      sx={{ gridColumn: { xs: 'span 12', sm: `span ${span}` }, minWidth: 0 }}
                    >
                      {node}
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <TextField
                label={t('worker:submitRequest.labelDetails')}
                value={(formData.details as string) || ''}
                onChange={(e) => updateField('details', e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={4}
                placeholder={t('worker:submitRequest.placeholderDetails')}
              />
            )}

            <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 1 }}>
              <Button
                size="small"
                onClick={() => resubmitFrom
                  ? navigate(`/me/requests/${resubmitFrom}`)
                  : setStep(0)}
                startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none' }}
              >
                {t('worker:submitRequest.back')}
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => setStep(2)}
                disabled={!canSubmit}
                endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none', borderRadius: 1 }}
              >
                {t('worker:submitRequest.review')}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}


      {step === 2 && selected && (
        <Paper elevation={0} sx={{ width: '100%', maxWidth: PORTAL_FORM_MAX_WIDTH, p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          {/* Form data summary — confirms what worker is about to submit */}
          {Object.keys(formData).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 700 }}>{t('worker:submitRequest.submissionSummary')}</Typography>
              <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.02)', border: '1px solid', borderColor: 'divider' }}>
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={1.5}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100, fontWeight: 500 }}>Process:</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{localizedProcessName(selected, t)}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100, fontWeight: 500 }}>Title:</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{title}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100, fontWeight: 500 }}>Priority:</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{priority}</Typography>
                  </Stack>
                  {Object.entries(formData).map(([k, v]) => {
                    const field = fields.find((candidate, index) => processFormFieldKey(candidate, index) === k);
                    const fieldIndex = field ? fields.indexOf(field) : -1;
                    const displayLabel = field
                      ? localizedProcessFieldLabel(selected.form_schema, field, fieldIndex, t)
                      : k;
                    const displayValue = localizedProcessFieldValue(selected.form_schema, field, v, t);
                    const fieldType = field?.type;
                    const reviewKind = processFormFieldKind(fieldType);
                    // `formData` holds ids for the two reference kinds — that is what
                    // the submit path accepts — so a review that printed the raw value
                    // would show `wrk_…` / `dep_…` back at the person confirming it.
                    const referenceName = reviewKind === 'worker'
                      ? workerPicked[k]?.name
                      : reviewKind === 'department'
                        ? departmentDisplayName(departments.find((row) => row.id === v))
                        : undefined;
                    const staged = Object.values(stagedAttachments);
                    return (
                      <Stack key={k} direction="row" spacing={1.5} alignItems="flex-start">
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100, fontWeight: 500 }}>{displayLabel}:</Typography>
                        {field && (processFormFieldKind(fieldType) === 'location' || processFormFieldKind(fieldType) === 'attachment') ? (
                          <ProcessFormResourceValue
                            fieldId={k}
                            fieldType={fieldType}
                            value={processFormFieldKind(fieldType) === 'location'
                              ? locations.find((location) => location.id === v)
                              : v}
                            attachments={staged}
                            t={t}
                          />
                        ) : (
                          <Typography variant="caption">{referenceName ?? String(displayValue ?? '—')}</Typography>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            </Box>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 700 }}>{t('worker:submitRequest.approvalChain')}</Typography>
          {(() => {
            const flow = selected.flow;
            if (!flow?.nodes?.length) {
              return <Typography variant="body2" color="text.secondary">{t('worker:submitRequest.noApprovalFlow')}</Typography>;
            }
            const approvalNodes = flow.nodes.filter((n) => n.type === 'approval');

            // Resolver display: pretty label for the resolver kind
            const resolverLabel = (cfg: any): string => {
              if (!cfg) return t('worker:submitRequest.resolverManager');
              if (cfg.resolver?.kind === 'manager_chain') {
                const lvl = cfg.resolver.level || 1;
                return lvl === 1 ? t('worker:submitRequest.resolverDirectManager') : t('worker:submitRequest.resolverManagerLevel', { lvl });
              }
              if (cfg.resolver?.kind === 'role') return t('worker:submitRequest.resolverRole', { role: cfg.resolver.role_code || '—' });
              if (cfg.resolver?.kind === 'specific_worker') return t('worker:submitRequest.resolverSpecific');
              if (cfg.approver_role === 'manager') return t('worker:submitRequest.resolverDirectManager');
              if (cfg.approver_role) return t('worker:submitRequest.resolverRole', { role: cfg.approver_role });
              return t('worker:submitRequest.resolverManager');
            };

            return (
              <Stack spacing={1.25}>
                {approvalNodes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">{t('worker:submitRequest.noApprovalSteps')}</Typography>
                ) : (
                  approvalNodes.map((n, i) => {
                    const targetHours = approvalTargetHours(n.data?.config);
                    return (
                      <Stack key={n.id} direction="row" alignItems="center" spacing={1.25}>
                        <Chip
                          label={i + 1}
                          size="small"
                          sx={{ width: 24, height: 24, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'primary.main', color: '#fff' }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                            {n.data?.label || t('worker:submitRequest.approvalStep')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            → {resolverLabel(n.data?.config)}
                          </Typography>
                        </Box>
                        {targetHours != null && (
                          <Chip
                            label={t('worker:submitRequest.approvalTarget', {
                              time: formatApprovalTargetHours(targetHours, i18n.resolvedLanguage ?? i18n.language),
                            })}
                            size="small"
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.7rem', borderRadius: 1 }}
                          />
                        )}
                      </Stack>
                    );
                  })
                )}
              </Stack>
            );
          })()}

          {/* CC recipients — async colleague picker (P0.7 follow-up).
              Uses /v1/me/colleagues for worker-level access; the
              admin /workers endpoint requires elevated perms not
              available to a worker on their own request submission. */}
          {!resubmitFrom && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                {t('worker:submitRequest.notifyOthers')}
              </Typography>
              <ColleaguePickerMulti
                value={ccPicked}
                onChange={setCcPicked}
                placeholder={t('worker:submitRequest.placeholderCC')}
                helperText={t('worker:submitRequest.helperCC')}
              />
            </Box>
          )}

          <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 3 }}>
            <Button size="small" onClick={() => setStep(1)} startIcon={<ArrowBack sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none' }}>
              {t('worker:submitRequest.back')}
            </Button>
            <Button
              variant="contained" size="small" onClick={handleSubmit}
              disabled={!canSubmit || submitting} endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', borderRadius: 1 }}
            >
              {submitting
                ? t('worker:submitRequest.submittingBtn')
                : t(resubmitFrom ? 'worker:requestDetail.resubmit' : 'worker:submitRequest.submitBtn')}
            </Button>
          </Stack>
        </Paper>
      )}

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={toast?.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkerSubmitRequest;
